<template>
    <div
        v-if="isOpen"
        class="activity-tracker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="activity-tracker-title"
        aria-describedby="activity-tracker-description"
    >
        <section
            ref="dialog"
            class="activity-tracker__panel"
            :class="{
                'activity-tracker__panel--visible':
                    isPanelVisible
            }"
        >
            <header class="activity-tracker__header">
                <div>
                    <h2
                        id="activity-tracker-title"
                        class="activity-tracker__title"
                    >
                        Activity Timesheets
                    </h2>

                    <p
                        id="activity-tracker-description"
                        class="activity-tracker__subtitle"
                    >
                        Review availability and status
                        duration for a selected period.
                    </p>
                </div>

                <button
                    ref="closeButton"
                    type="button"
                    class="activity-tracker__close"
                    aria-label="Close activity timesheets"
                    :disabled="isLoading"
                    @click="closePopup"
                >
                    <i
                        class="fa fa-times"
                        aria-hidden="true"
                    ></i>
                </button>
            </header>

            <main class="activity-tracker__content">
                <form
                    class="activity-tracker__filters"
                    novalidate
                    @submit.prevent="applyFilters"
                >
                    <div class="activity-tracker__field">
                        <label
                            class="activity-tracker__label"
                            for="activity-start-date"
                        >
                            Start date
                        </label>

                        <datetime
                            id="activity-start-date"
                            v-model="startDateInput"
                            name="activity_start_date"
                            v-validate="'required'"
                            type="datetime"
                            input-class="
                                activity-tracker__date-input
                            "
                            :class="{
                                'activity-tracker__datetime--invalid':
                                    errors.has(
                                        'activity_start_date'
                                    )
                            }"
                            placeholder="Select start date"
                            :phrases="datetimePhrases"
                            :week-start="7"
                            :max-datetime="maxDatetime"
                            use12-hour
                            auto
                        ></datetime>

                        <small
                            v-if="
                                errors.has(
                                    'activity_start_date'
                                )
                            "
                            class="activity-tracker__field-error"
                            role="alert"
                        >
                            Start date is required.
                        </small>
                    </div>

                    <div class="activity-tracker__field">
                        <label
                            class="activity-tracker__label"
                            for="activity-end-date"
                        >
                            End date
                        </label>

                        <datetime
                            id="activity-end-date"
                            v-model="endDateInput"
                            name="activity_end_date"
                            v-validate="'required'"
                            type="datetime"
                            input-class="
                                activity-tracker__date-input
                            "
                            :class="{
                                'activity-tracker__datetime--invalid':
                                    errors.has(
                                        'activity_end_date'
                                    )
                            }"
                            placeholder="Select end date"
                            :phrases="datetimePhrases"
                            :week-start="7"
                            :min-datetime="
                                startDateInput || undefined
                            "
                            :max-datetime="maxDatetime"
                            use12-hour
                            auto
                        ></datetime>

                        <small
                            v-if="
                                errors.has(
                                    'activity_end_date'
                                )
                            "
                            class="activity-tracker__field-error"
                            role="alert"
                        >
                            End date is required.
                        </small>
                    </div>

                    <div class="activity-tracker__filter-action">
                        <button
                            type="submit"
                            class="activity-tracker__submit"
                            :disabled="isLoading"
                        >
                            <i
                                v-if="isLoading"
                                class="fa fa-spinner fa-spin"
                                aria-hidden="true"
                            ></i>

                            <span>
                                {{
                                    isLoading
                                        ? 'Loading...'
                                        : 'Apply Filter'
                                }}
                            </span>
                        </button>
                    </div>
                </form>

                <p
                    v-if="filterError"
                    class="activity-tracker__error"
                    role="alert"
                    aria-live="assertive"
                >
                    <i
                        class="fa fa-exclamation-circle"
                        aria-hidden="true"
                    ></i>

                    {{ filterError }}
                </p>

                <div class="activity-tracker__legend">
                    <p class="activity-tracker__hint">
                        Select or focus an activity bar to view
                        the status breakdown.
                    </p>

                    <ul
                        class="activity-tracker__legend-list"
                        aria-label="Activity status colors"
                    >
                        <li
                            v-for="status in activityStatuses"
                            :key="status.key"
                            class="activity-tracker__legend-item"
                        >
                            <span
                                class="
                                    activity-tracker__legend-marker
                                "
                                :class="
                                    'activity-tracker__legend-marker--' +
                                        status.key
                                "
                                aria-hidden="true"
                            ></span>

                            {{ status.label }}
                        </li>
                    </ul>
                </div>

                <div
                    v-if="isInitialLoading"
                    class="activity-tracker__loading"
                    role="status"
                    aria-live="polite"
                >
                    <div
                        class="activity-tracker__spinner"
                        aria-hidden="true"
                    ></div>

                    <span>Loading activity timesheets...</span>
                </div>

                <div
                    v-else-if="activityResults.length"
                    class="activity-tracker__table-wrapper"
                >
                    <table class="activity-tracker__table">
                        <caption class="sr-only">
                            Team activity timesheets from
                            {{ formattedStartDate }} to
                            {{ formattedEndDate }}
                        </caption>

                        <thead>
                            <tr>
                                <th scope="col">Member</th>
                                <th scope="col">Department</th>
                                <th scope="col">Start date</th>
                                <th scope="col">End date</th>
                                <th scope="col">Online activity</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="item in activityResults"
                                :key="getRowKey(item)"
                            >
                                <td data-label="Member">
                                    <router-link
                                        class="
                                            activity-tracker__member
                                        "
                                        :to="getMemberRoute(item)"
                                    >
                                        <img
                                            class="
                                                activity-tracker__avatar
                                            "
                                            :src="getAvatar(item)"
                                            :alt="
                                                getMemberName(item) +
                                                    ' profile image'
                                            "
                                            width="50"
                                            height="50"
                                            @error="
                                                handleAvatarError
                                            "
                                        >

                                        <span
                                            class="
                                                activity-tracker__member-info
                                            "
                                        >
                                            <span
                                                class="
                                                    activity-tracker__member-name
                                                "
                                            >
                                                {{
                                                    getMemberName(
                                                        item
                                                    )
                                                }}
                                            </span>

                                            <!-- <span
                                                class="
                                                    activity-tracker__member-email
                                                "
                                            >
                                                {{
                                                    item.email ||
                                                        'Email unavailable'
                                                }}
                                            </span> -->
                                        </span>
                                    </router-link>
                                </td>

                                <td data-label="Department">
                                    {{
                                        item.department ||
                                            'Not assigned'
                                    }}
                                </td>

                                <td data-label="Start date">
                                    {{ formattedStartDate }}
                                </td>

                                <td data-label="End date">
                                    {{ formattedEndDate }}
                                </td>

                                <td data-label="Online activity">
                                    <div
                                        class="
                                            activity-tracker__activity
                                        "
                                    >
                                        <button
                                            type="button"
                                            class="
                                                activity-tracker__bar
                                            "
                                            :aria-label="
                                                getActivityAriaLabel(
                                                    item
                                                )
                                            "
                                            @click="
                                                toggleBreakdown(
                                                    item
                                                )
                                            "
                                            @focus="
                                                openBreakdown(item)
                                            "
                                            @blur="
                                                closeBreakdownLater(
                                                    item
                                                )
                                            "
                                        >
                                            <span
                                                v-for="
                                                    status in
                                                        activityStatuses
                                                "
                                                :key="status.key"
                                                class="
                                                    activity-tracker__bar-section
                                                "
                                                :class="
                                                    'activity-tracker__bar-section--' +
                                                        status.key
                                                "
                                                :style="
                                                    getSegmentStyle(
                                                        item,
                                                        status.key
                                                    )
                                                "
                                            ></span>
                                        </button>

                                        <div
                                            v-show="
                                                isBreakdownOpen(
                                                    item
                                                )
                                            "
                                            class="
                                                activity-tracker__breakdown
                                            "
                                            role="tooltip"
                                        >
                                            <dl
                                                class="
                                                    activity-tracker__breakdown-list
                                                "
                                            >
                                                <div
                                                    class="
                                                        activity-tracker__breakdown-row
                                                    "
                                                >
                                                    <dt>
                                                        Total login
                                                        time
                                                    </dt>

                                                    <dd>
                                                        {{
                                                            formatHours(
                                                                item.total_login_time
                                                            )
                                                        }}
                                                    </dd>
                                                </div>

                                                <div
                                                    v-for="
                                                        status in
                                                            activityStatuses
                                                    "
                                                    :key="status.key"
                                                    class="
                                                        activity-tracker__breakdown-row
                                                    "
                                                >
                                                    <dt>
                                                        {{
                                                            status.label
                                                        }}
                                                    </dt>

                                                    <dd>
                                                        {{
                                                            formatStatusDuration(
                                                                item,
                                                                status.key
                                                            )
                                                        }}
                                                    </dd>
                                                </div>
                                            </dl>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div
                    v-else
                    class="activity-tracker__empty"
                    role="status"
                >
                    <i
                        class="fa fa-clock-o"
                        aria-hidden="true"
                    ></i>

                    <h3>No activity records found</h3>

                    <p>
                        No activity was recorded for the selected
                        date range.
                    </p>
                </div>
            </main>

            <div
                v-if="isLoading && !isInitialLoading"
                class="activity-tracker__loading-overlay"
                role="status"
                aria-live="polite"
            >
                <div
                    class="activity-tracker__spinner"
                    aria-hidden="true"
                ></div>

                <span>Updating activity records...</span>
            </div>
        </section>
    </div>
</template>

<script>
import 'vue-datetime/dist/vue-datetime.css';

import { Datetime } from 'vue-datetime';
import { bus } from '@/main';
import toast from '@/services/toast';
import fallbackAvatar from '@/assets/images/user2.png';

const BODY_SCROLL_CLASS =
    'body_right_scroll_remove';

const CLOSE_ANIMATION_DURATION = 700;

export default {
    name: 'IndividualActivityTracker',

    components: {
        Datetime
    },

    data() {
        return {
            activityData: null,
            activityResults: [],
            startDateInput: '',
            endDateInput: '',
            appliedStartDate: '',
            appliedEndDate: '',
            maxDatetime: '',
            isOpen: false,
            isPanelVisible: false,
            isLoading: false,
            isInitialLoading: true,
            filterError: '',
            activeBreakdownId: null,
            closeTimer: null,
            breakdownTimer: null,
            previouslyFocusedElement: null,
            listenersAdded: false,
            datetimePhrases: {
                ok: 'Apply',
                cancel: 'Cancel'
            },
            activityStatuses: [
                {
                    key: 'available',
                    label: 'Available'
                },
                {
                    key: 'busy',
                    label: 'Busy'
                },
                {
                    key: 'meeting',
                    label: 'In a meeting'
                },
                {
                    key: 'brb',
                    label: 'Be right back'
                },
                {
                    key: 'lunch',
                    label: 'Lunch'
                }
            ]
        };
    },

    computed: {
        formattedStartDate() {
            return this.formatDateTime(
                this.appliedStartDate
            );
        },

        formattedEndDate() {
            return this.formatDateTime(
                this.appliedEndDate
            );
        }
    },

    mounted() {
        this.initializePopup();
    },

    beforeDestroy() {
        this.clearTimers();
        this.removeGlobalListeners();
    },

    methods: {
        async initializePopup() {
            const now = new Date();
            const previousDay = new Date(
                now.getTime()
            );

            previousDay.setDate(
                previousDay.getDate() - 1
            );

            this.maxDatetime = now.toISOString();
            this.startDateInput =
                previousDay.toISOString();
            this.endDateInput = now.toISOString();
            this.appliedStartDate =
                this.startDateInput;
            this.appliedEndDate =
                this.endDateInput;

            this.previouslyFocusedElement =
                document.activeElement;

            this.isOpen = true;
            this.addGlobalListeners();

            window.scrollTo({
                top: 0,
                left: 0
            });

            this.$nextTick(() => {
                window.requestAnimationFrame(() => {
                    this.isPanelVisible = true;

                    if (this.$refs.closeButton) {
                        this.$refs.closeButton.focus();
                    }
                });
            });

            await this.fetchActivityData({
                start_date: this.appliedStartDate,
                end_date: this.appliedEndDate
            });

            this.isInitialLoading = false;
        },

        async applyFilters() {
            if (this.isLoading) {
                return;
            }

            const isValid =
                await this.$validator.validateAll();

            if (!isValid) {
                return;
            }

            if (
                !this.isValidDateRange(
                    this.startDateInput,
                    this.endDateInput
                )
            ) {
                this.filterError =
                    'End date must be later than the start date.';

                toast.error(this.filterError);
                return;
            }

            this.filterError = '';

            const payload = {
                start_date: this.startDateInput,
                end_date: this.endDateInput
            };

            const wasSuccessful =
                await this.fetchActivityData(
                    payload
                );

            if (!wasSuccessful) {
                return;
            }

            this.appliedStartDate =
                this.startDateInput;
            this.appliedEndDate =
                this.endDateInput;
        },

        async fetchActivityData(payload) {
            if (this.isLoading) {
                return false;
            }

            this.isLoading = true;
            this.filterError = '';

            try {
                const response =
                    await this.$store.dispatch(
                        'selfUserActivityTrackerMethod',
                        payload
                    );

                const data =
                    this.extractResponseData(
                        response
                    );

                this.activityData = data;
                this.activityResults =
                    data &&
                    Array.isArray(data.results)
                        ? data.results
                        : [];

                return true;
            } catch (error) {
                this.activityData = null;
                this.activityResults = [];

                this.filterError =
                    this.getErrorMessage(
                        error,
                        'Unable to load activity timesheets. Please try again.'
                    );

                toast.error(this.filterError);

                console.error(
                    'Unable to load activity timesheets:',
                    error
                );

                return false;
            } finally {
                this.isLoading = false;
            }
        },

        extractResponseData(response) {
            if (
                response &&
                response.data &&
                response.data.data !== undefined
            ) {
                return response.data.data;
            }

            if (
                response &&
                response.data !== undefined
            ) {
                return response.data;
            }

            return null;
        },

        isValidDateRange(startDate, endDate) {
            const startTimestamp =
                new Date(startDate).getTime();

            const endTimestamp =
                new Date(endDate).getTime();

            return (
                Number.isFinite(startTimestamp) &&
                Number.isFinite(endTimestamp) &&
                startTimestamp <= endTimestamp &&
                endTimestamp <=
                    new Date(
                        this.maxDatetime
                    ).getTime()
            );
        },

        getRowKey(item) {
            return (
                item.user_id ||
                item.id ||
                item.email
            );
        },

        getMemberRoute(item) {
            return {
                name: 'memberUserDetails',
                params: {
                    userDetailsId:
                        item.user_id || item.id
                }
            };
        },

        getMemberName(item) {
            if (!item) {
                return 'Unknown member';
            }

            const fullName = [
                item.salutation || '',
                item.first_name || '',
                item.last_name || ''
            ]
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim();

            return (
                fullName ||
                item.email ||
                'Unknown member'
            );
        },

        getAvatar(item) {
            return (
                item &&
                item.thumbnail
                    ? item.thumbnail
                    : fallbackAvatar
            );
        },

        handleAvatarError(event) {
            if (!event || !event.target) {
                return;
            }

            event.target.onerror = null;
            event.target.src = fallbackAvatar;
        },

        getActivityMinutes(item, key) {
            if (!item) {
                return 0;
            }

            const fieldMap = {
                available: 'available_time',
                busy: 'busy_time',
                meeting: 'meeting_time',
                brb: 'brb_time',
                lunch: 'lunch_time'
            };

            const value =
                this.toSafeNumber(
                    item[fieldMap[key]]
                );

            /*
             * The existing API/UI represents available time
             * in hours and the remaining statuses in minutes.
             */
            if (key === 'available') {
                return value * 60;
            }

            return value;
        },

        getTotalLoginMinutes(item) {
            return (
                this.toSafeNumber(
                    item && item.total_login_time
                ) * 60
            );
        },

        getActivityPercentage(item, key) {
            const totalMinutes =
                this.getTotalLoginMinutes(item);

            if (totalMinutes <= 0) {
                return 0;
            }

            const percentage =
                (
                    this.getActivityMinutes(
                        item,
                        key
                    ) /
                    totalMinutes
                ) * 100;

            return Math.min(
                100,
                Math.max(0, percentage)
            );
        },

        getSegmentStyle(item, key) {
            return {
                width:
                    this.getActivityPercentage(
                        item,
                        key
                    ) + '%'
            };
        },

        getActivityAriaLabel(item) {
            const values =
                this.activityStatuses.map(
                    (status) => {
                        return (
                            status.label +
                            ': ' +
                            this.formatStatusDuration(
                                item,
                                status.key
                            )
                        );
                    }
                );

            return (
                'Activity breakdown for ' +
                this.getMemberName(item) +
                '. ' +
                values.join('. ')
            );
        },

        formatStatusDuration(item, key) {
            if (key === 'available') {
                return this.formatHours(
                    item && item.available_time
                );
            }

            const fieldMap = {
                busy: 'busy_time',
                meeting: 'meeting_time',
                brb: 'brb_time',
                lunch: 'lunch_time'
            };

            return this.formatMinutes(
                item &&
                    item[fieldMap[key]]
            );
        },

        formatHours(value) {
            return (
                this.formatNumber(value) +
                ' hours'
            );
        },

        formatMinutes(value) {
            return (
                this.formatNumber(value) +
                ' minutes'
            );
        },

        formatNumber(value) {
            return this.toSafeNumber(
                value
            ).toFixed(2);
        },

        toSafeNumber(value) {
            const numberValue =
                Number(value);

            return Number.isFinite(numberValue)
                ? numberValue
                : 0;
        },

        formatDateTime(value) {
            if (!value) {
                return 'Not available';
            }

            const date = new Date(value);

            if (
                Number.isNaN(date.getTime())
            ) {
                return 'Not available';
            }

            return new Intl.DateTimeFormat(
                undefined,
                {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: 'numeric',
                    minute: '2-digit'
                }
            ).format(date);
        },

        toggleBreakdown(item) {
            const itemId =
                this.getRowKey(item);

            this.activeBreakdownId =
                this.activeBreakdownId ===
                itemId
                    ? null
                    : itemId;
        },

        openBreakdown(item) {
            this.clearBreakdownTimer();

            this.activeBreakdownId =
                this.getRowKey(item);
        },

        closeBreakdownLater(item) {
            this.clearBreakdownTimer();

            this.breakdownTimer =
                window.setTimeout(() => {
                    if (
                        this.activeBreakdownId ===
                        this.getRowKey(item)
                    ) {
                        this.activeBreakdownId =
                            null;
                    }
                }, 150);
        },

        isBreakdownOpen(item) {
            return (
                this.activeBreakdownId ===
                this.getRowKey(item)
            );
        },

        getErrorMessage(
            error,
            fallbackMessage
        ) {
            const responseData =
                error &&
                error.response &&
                error.response.data
                    ? error.response.data
                    : error && error.data
                        ? error.data
                        : null;

            if (!responseData) {
                return (
                    error &&
                    error.message
                        ? error.message
                        : fallbackMessage
                );
            }

            const nestedData =
                responseData.data &&
                typeof responseData.data ===
                    'object'
                    ? responseData.data
                    : null;

            return (
                this.getFirstError(
                    nestedData
                ) ||
                this.getFirstError(
                    responseData
                ) ||
                responseData.user_msg ||
                responseData.msg ||
                responseData.message ||
                responseData.detail ||
                fallbackMessage
            );
        },

        getFirstError(data) {
            if (
                !data ||
                typeof data !== 'object'
            ) {
                return '';
            }

            const keys = Object.keys(data);

            for (
                let index = 0;
                index < keys.length;
                index += 1
            ) {
                const value =
                    data[keys[index]];

                if (
                    Array.isArray(value) &&
                    value.length
                ) {
                    return String(value[0]);
                }
            }

            return '';
        },

        handleKeydown(event) {
            if (
                event &&
                event.key === 'Escape' &&
                !this.isLoading
            ) {
                this.closePopup();
            }
        },

        addGlobalListeners() {
            if (this.listenersAdded) {
                return;
            }

            document.body.classList.add(
                BODY_SCROLL_CLASS
            );

            document.addEventListener(
                'keydown',
                this.handleKeydown
            );

            this.listenersAdded = true;
        },

        removeGlobalListeners() {
            if (!this.listenersAdded) {
                return;
            }

            document.body.classList.remove(
                BODY_SCROLL_CLASS
            );

            document.removeEventListener(
                'keydown',
                this.handleKeydown
            );

            this.listenersAdded = false;
        },

        closePopup() {
            if (
                this.isLoading ||
                !this.isOpen
            ) {
                return;
            }

            this.isPanelVisible = false;
            this.removeGlobalListeners();
            this.clearCloseTimer();

            this.closeTimer =
                window.setTimeout(() => {
                    this.isOpen = false;

                    bus.$emit(
                        'individualActivityTrackerStateBus',
                        false
                    );

                    this.restoreFocus();
                }, CLOSE_ANIMATION_DURATION);
        },

        restoreFocus() {
            const previousElement =
                this.previouslyFocusedElement;

            this.$nextTick(() => {
                if (
                    previousElement &&
                    document.body.contains(
                        previousElement
                    ) &&
                    typeof previousElement.focus ===
                        'function'
                ) {
                    previousElement.focus();
                }

                this.previouslyFocusedElement =
                    null;
            });
        },

        clearCloseTimer() {
            if (!this.closeTimer) {
                return;
            }

            window.clearTimeout(
                this.closeTimer
            );

            this.closeTimer = null;
        },

        clearBreakdownTimer() {
            if (!this.breakdownTimer) {
                return;
            }

            window.clearTimeout(
                this.breakdownTimer
            );

            this.breakdownTimer = null;
        },

        clearTimers() {
            this.clearCloseTimer();
            this.clearBreakdownTimer();
        }
    }
};
</script>

<style lang="scss" scoped>
@import url(
    '../../../../assets/fonts/acetone/style.css'
);

$overlay: rgba(0, 0, 0, 0.94);
$surface: #080808;
$surface-muted: rgba(228, 233, 237, 0.1);
$border: rgba(255, 255, 255, 0.05);
$text-primary: #ffffff;
$text-secondary: rgba(255, 255, 255, 0.68);
$accent: #ffd400;
$accent-secondary: #87cefa;
$error: #ff6b6b;
$available: #38b124;
$busy: #ee0809;
$meeting: #f66c11;
$brb: #f3d32d;
$lunch: #1483e5;

.activity-tracker {
    position: fixed;
    inset: 0;
    z-index: 1001;
    overflow: hidden;
    background-color: rgba(0, 0, 0, 0.5);
    font-family:
        'MyriadProRegular',
        'Lato',
        sans-serif;
        letter-spacing: 0.5px;

    &__panel {
        position: fixed;
        inset: 0;
        overflow-y: auto;
        color: $text-primary;
        background-color: $overlay;
        transform: translateX(100%);
        transition:
            transform 0.7s
            cubic-bezier(
                0.42,
                1.14,
                1,
                1
            );

        &--visible {
            transform: translateX(0);
        }
    }

    &__header {
        position: sticky;
        top: 0;
        z-index: 20;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 20px;
        padding: 20px 28px;
        border-bottom: 1px solid $border;
        background-color: $surface;
    }

    &__title {
        margin: 0;
        color: $accent;
        font-size: 28px;
        font-weight: 600;
        line-height: 1.25;
    }

    &__subtitle {
        margin: 6px 0 0;
        color: $text-secondary;
        font-size: 15px;
        line-height: 1.4;
    }

    &__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 40px;
        width: 40px;
        height: 40px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        color: $accent;
        background-color: transparent;
        font-size: 24px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
            color: #111111;
            background-color: $accent-secondary;
        }

        &:focus {
            outline: 2px solid $accent;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    &__content {
        position: relative;
        max-width: 1600px;
        min-height: calc(100vh - 102px);
        margin: 0 auto;
        padding: 24px 28px 40px;
    }

    &__filters {
        display: grid;
        grid-template-columns:
            minmax(220px, 1fr)
            minmax(220px, 1fr)
            auto;
        align-items: start;
        gap: 16px;
        max-width: 820px;
        margin-left: auto;
    }

    &__field {
        min-width: 0;
    }

    &__label {
        display: block;
        margin-bottom: 6px;
        color: $text-primary;
        font-size: 16px;
        font-weight: 600;
    }

    &__field-error {
        display: block;
        margin-top: 5px;
        color: $error;
        font-size: 16px;
    }

    &__filter-action {
        padding-top: 24px;
    }

    &__submit {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 130px;
        min-height: 42px;
        padding: 10px 18px;
        border: 1px solid transparent;
        border-radius: 24px;
        color: $accent;
        background-color: rgba(
            255,
            255,
            255,
            0.1
        );
        font-family: inherit;
        font-size: 16px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
            color: #111111;
            background-color: $accent-secondary;
        }

        &:focus {
            outline: 2px solid $accent;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }
    }

    &__error {
        margin: 18px 0 0;
        padding: 12px 14px;
        border: 1px solid rgba(
            255,
            107,
            107,
            0.35
        );
        border-radius: 5px;
        color: $error;
        background-color: rgba(
            255,
            107,
            107,
            0.08
        );
        font-size: 16px;

        .fa {
            margin-right: 7px;
        }
    }

    &__legend {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 12px 20px;
        margin: 20px 0;
    }

    &__hint {
        margin: 0;
        color: $accent;
        font-size: 16px;
    }

    &__legend-list {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px 16px;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__legend-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: $text-secondary;
        font-size: 16px;
    }

    &__legend-marker {
        width: 10px;
        height: 10px;
        border-radius: 50%;

        &--available {
            background-color: $available;
        }

        &--busy {
            background-color: $busy;
        }

        &--meeting {
            background-color: $meeting;
        }

        &--brb {
            background-color: $brb;
        }

        &--lunch {
            background-color: $lunch;
        }
    }

    &__table-wrapper {
        // overflow-x: auto;
        border-top: 1px solid $border;
        padding-top: 6px;
    }

    &__table {
        width: 100%;
        margin: 0;
        border-collapse: separate;
        border-spacing: 0 4px;
        color: $text-primary;

        th {
            padding: 11px 10px;
            border: 0;
            color: $accent-secondary;
            background-color: $surface-muted;
            font-size: 18px;
            font-weight: 600;
            line-height: 1.3;
            text-align: left;
            text-transform: uppercase;
        }

        td {
            padding: 11px 10px;
            border: 0;
            color: $text-primary;
            background-color: $surface-muted;
            font-size: 18px;
            line-height: 1.4;
            vertical-align: middle;
        }

        th:nth-child(1) {
            width: 24%;
        }

        th:nth-child(2) {
            width: 17%;
        }

        th:nth-child(3),
        th:nth-child(4) {
            width: 18%;
        }

        th:nth-child(5) {
            width: 23%;
        }
    }

    &__member {
        display: flex;
        align-items: center;
        min-width: 0;
        color: inherit;
        text-decoration: none;

        &:hover,
        &:focus {
            text-decoration: none;
        }

        &:focus {
            outline: 2px solid $accent;
            outline-offset: 3px;
        }
    }

    &__avatar {
        flex: 0 0 50px;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        object-fit: cover;
    }

    &__member-info {
        min-width: 0;
        margin-left: 10px;
    }

    &__member-name {
        display: block;
        color: $text-primary;
        overflow-wrap: anywhere;
    }

    &__member-email {
        display: block;
        margin-top: 2px;
        color: $text-secondary;
        font-size: 16px;
        overflow-wrap: anywhere;
    }

    &__activity {
        position: relative;
        min-width: 220px;
    }

    &__bar {
        display: flex;
        width: 100%;
        height: 16px;
        padding: 0;
        overflow: hidden;
        border: 1px solid rgba(
            255,
            255,
            255,
            0.16
        );
        border-radius: 8px;
        background-color: rgba(
            255,
            255,
            255,
            0.08
        );
        cursor: pointer;

        &:focus {
            outline: 2px solid $accent;
            outline-offset: 3px;
        }
    }

    &__bar-section {
        display: block;
        height: 100%;
        min-width: 0;
        transition: width 0.25s ease;

        &--available {
            background-color: $available;
        }

        &--busy {
            background-color: $busy;
        }

        &--meeting {
            background-color: $meeting;
        }

        &--brb {
            background-color: $brb;
        }

        &--lunch {
            background-color: $lunch;
        }
    }

    &__breakdown {
        position: absolute;
        top: calc(100% + 12px);
        right: 0;
        z-index: 30;
        width: 320px;
        max-width: calc(100vw - 40px);
        padding: 14px 16px;
        border: 2px solid $accent;
        border-radius: 4px;
        background-color: rgba(
            0,
            0,
            0,
            0.97
        );
        box-shadow:
            0 8px 24px rgba(
                0,
                0,
                0,
                0.45
            );

        &::before {
            position: absolute;
            top: -7px;
            right: 24px;
            width: 12px;
            height: 12px;
            border-top: 2px solid $accent;
            border-left: 2px solid $accent;
            background-color: #000000;
            content: '';
            transform: rotate(45deg);
        }
    }

    &__breakdown-list {
        margin: 0;
    }

    &__breakdown-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 9px;
        color: #f7f5bc;
        font-size: 16px;

        &:last-child {
            margin-bottom: 0;
        }

        dt {
            font-weight: 600;
        }

        dd {
            margin: 0;
            text-align: right;
            white-space: nowrap;
        }
    }

    &__loading,
    &__empty {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        min-height: 300px;
        padding: 30px;
        color: $text-secondary;
        text-align: center;
    }

    &__empty {
        .fa {
            margin-bottom: 12px;
            color: $accent;
            font-size: 42px;
        }

        h3 {
            margin: 0;
            color: $text-primary;
            font-size: 21px;
        }

        p {
            margin: 8px 0 0;
            font-size: 16px;
        }
    }

    &__spinner {
        width: 34px;
        height: 34px;
        margin-bottom: 12px;
        border: 3px solid rgba(
            255,
            255,
            255,
            0.2
        );
        border-top-color: $accent;
        border-radius: 50%;
        animation:
            activity-tracker-spin
            0.8s linear infinite;
    }

    &__loading-overlay {
        position: fixed;
        inset: 0;
        z-index: 50;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        color: $text-primary;
        background-color: rgba(
            0,
            0,
            0,
            0.62
        );
    }
}

@keyframes activity-tracker-spin {
    to {
        transform: rotate(360deg);
    }
}

@media only screen and (max-width: 900px) {
    .activity-tracker {
        &__filters {
            grid-template-columns: 1fr 1fr;
        }

        &__filter-action {
            grid-column: 1 / -1;
            padding-top: 0;
        }

        &__submit {
            width: 100%;
        }
    }
}

@media only screen and (max-width: 767px) {
    .activity-tracker {
        &__header {
            padding: 16px;
        }

        &__title {
            font-size: 23px;
        }

        &__content {
            padding: 18px 14px 30px;
        }

        &__filters {
            grid-template-columns: 1fr;
        }

        &__filter-action {
            grid-column: auto;
        }

        &__legend {
            align-items: flex-start;
            justify-content: flex-start;
        }

        &__table-wrapper {
            overflow: visible;
        }

        &__table {
            display: block;

            thead {
                position: absolute;
                width: 1px;
                height: 1px;
                overflow: hidden;
                clip: rect(0 0 0 0);
                white-space: nowrap;
            }

            tbody,
            tr,
            td {
                display: block;
                width: 100%;
            }

            tr {
                margin-bottom: 12px;
                overflow: visible;
                border: 1px solid $border;
                border-radius: 6px;
            }

            td {
                position: relative;
                min-height: 42px;
                padding:
                    10px 10px
                    10px 43%;
                border-bottom:
                    1px solid
                    rgba(
                        255,
                        255,
                        255,
                        0.06
                    );
                font-size: 16px;

                &:last-child {
                    overflow: visible;
                    border-bottom: 0;
                }

                &::before {
                    position: absolute;
                    top: 11px;
                    left: 10px;
                    width: 38%;
                    color: $accent-secondary;
                    content: attr(data-label);
                    font-size: 16px;
                    font-weight: 600;
                }
            }
        }

        &__member {
            align-items: flex-start;
        }

        &__avatar {
            width: 42px;
            height: 42px;
            flex-basis: 42px;
        }

        &__activity {
            min-width: 0;
        }

        &__breakdown {
            right: 0;
            width: 320px;
            max-width: calc(100vw - 44px);
        }
    }
}

@media only screen and (max-width: 480px) {
    .activity-tracker {
        &__subtitle {
            display: none;
        }

        &__table {
            td {
                padding:
                    42px 10px
                    10px;

                &::before {
                    top: 10px;
                    width: calc(100% - 20px);
                }
            }
        }

        &__breakdown {
            right: auto;
            left: 0;

            &::before {
                right: auto;
                left: 24px;
            }
        }
    }
}
</style>